'use client'

import { useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { Box, Divider, Typography } from '@mui/material'
import {
  PreviewCard,
  PreviewCardData,
  FeatureCard,
  FeatureCardData,
} from '@common-components'
import { Image, ImageData } from '@media-components'
import { ScrollToElement } from '@utils/scroll'
import { CustomStyles, getStyles, useCardStyles } from '@utils/styles'
import defaultStyles, {
  StylesClassNames,
  stylesImageHeadingVariant,
} from './styles'

export type OurServicesData = {
  heading: string
  title?: string
  imageData?: ImageData
  description?: string
  featureCardsData?: FeatureCardData[]
  previewCardsData?: PreviewCardData[]
}

type OurServicesProps = OurServicesData & {
  csx?: CustomStyles<StylesClassNames>
  id?: string
  componentVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span'
}

export type OurServicesCustomStyles = CustomStyles<StylesClassNames>

export function OurServices({
  heading,
  title,
  description,
  imageData,
  featureCardsData,
  previewCardsData,
  csx,
  id,
  componentVariant = 'span',
}: OurServicesProps) {
  const hasImage = !!imageData?.src
  const styles = getStyles(
    defaultStyles,
    hasImage ? stylesImageHeadingVariant : {},
    csx
  )

  const { cardStyles: previewCardStyles } = useCardStyles({
    count: previewCardsData?.length || 0,
  })

  const searchParams = useSearchParams()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = searchParams.get('hash')
      if (hash) {
        ScrollToElement(hash)
      }
    }
  }, [searchParams])

  const modifiedId = id?.replace(/ /g, '_').toLowerCase()

  return (
    <Box {...styles('wrapper')} id={modifiedId}>
      <Box>
        <Box {...styles('infoContainer')}>
          {imageData?.src && <Image {...imageData} {...styles('image')} />}
          {title && !hasImage && (
            <Typography variant="h4.500" {...styles('heading')}>
              {title}
            </Typography>
          )}
          {title && hasImage && (
            <Typography variant="body3.500" {...styles('imageHeading')}>
              {title}
            </Typography>
          )}
          <Typography
            variant={hasImage ? 'h6.500' : 'h4.500'}
            {...styles('heading')}
            component={componentVariant}
          >
            {heading}
          </Typography>
          {description && (
            <Typography
              variant="body1"
              color="text.secondary"
              {...styles('description')}
            >
              {description}
            </Typography>
          )}
        </Box>
        {!!featureCardsData?.length && (
          <Box {...styles('featuresWrapper')}>
            {featureCardsData.map((card, idx) => (
              <FeatureCard
                key={idx}
                {...card}
                csx={{ wrapper: styles('featureCardStyles').sx }}
                animation="flip"
              />
            ))}
          </Box>
        )}

        {!!previewCardsData?.length && (
          <Box {...styles('previewsWrapper')}>
            {previewCardsData.map((card, idx) => {
              return (
                <PreviewCard
                  key={idx}
                  {...card}
                  csx={{
                    wrapper: { ...previewCardStyles, flex: 1 },
                  }}
                />
              )
            })}
          </Box>
        )}
      </Box>
      <Divider />
    </Box>
  )
}
